export interface ExportFormat {
  id: string;
  name: string;
  extension: string;
  description: string;
  icon: 'pdf' | 'excel' | 'csv' | 'print';
  supportsFilters: boolean;
  supportsMultiSheet: boolean;
}

export interface ExportScope {
  id: string;
  label: string;
  description: string;
}

export interface ExportPreset {
  id: string;
  name: string;
  description: string;
  format: string;
  scope: string;
  filters: {
    yearGroups?: string[];
    teachers?: string[];
    rooms?: string[];
  };
  options: {
    includeConflicts?: boolean;
    includeEmptySlots?: boolean;
    colorCoded?: boolean;
    pageOrientation?: 'portrait' | 'landscape';
  };
}

export interface ExportHistory {
  id: string;
  timestamp: string;
  format: string;
  scope: string;
  fileName: string;
  fileSize: string;
  downloadedBy: string;
  downloadedByName: string;
}

export const exportFormats: ExportFormat[] = [
  {
    id: 'pdf',
    name: 'PDF Document',
    extension: '.pdf',
    description: 'High-quality printable format, suitable for distribution',
    icon: 'pdf',
    supportsFilters: true,
    supportsMultiSheet: false,
  },
  {
    id: 'excel',
    name: 'Excel Spreadsheet',
    extension: '.xlsx',
    description: 'Editable spreadsheet with multiple worksheets',
    icon: 'excel',
    supportsFilters: true,
    supportsMultiSheet: true,
  },
  {
    id: 'csv',
    name: 'CSV File',
    extension: '.csv',
    description: 'Plain text format for data import/export',
    icon: 'csv',
    supportsFilters: true,
    supportsMultiSheet: false,
  },
  {
    id: 'print',
    name: 'Print Preview',
    extension: '',
    description: 'Opens browser print dialog',
    icon: 'print',
    supportsFilters: true,
    supportsMultiSheet: false,
  },
];

export const exportScopes: ExportScope[] = [
  { id: 'full', label: 'Full Timetable', description: 'Export complete timetable with all year groups' },
  { id: 'year-group', label: 'By Year Group', description: 'Export timetables for selected year groups' },
  { id: 'teacher', label: 'By Teacher', description: 'Export individual teacher timetables' },
  { id: 'room', label: 'By Room', description: 'Export room allocation schedules' },
  { id: 'class', label: 'By Class', description: 'Export class-specific timetables' },
];

export const mockExportPresets: ExportPreset[] = [
  {
    id: 'preset-1',
    name: 'All Year Groups PDF',
    description: 'Full timetable for all year groups in PDF format',
    format: 'pdf',
    scope: 'full',
    filters: {},
    options: {
      includeConflicts: false,
      includeEmptySlots: true,
      colorCoded: true,
      pageOrientation: 'landscape',
    },
  },
  {
    id: 'preset-2',
    name: 'Teacher Timetables Excel',
    description: 'Individual teacher timetables in Excel with one sheet per teacher',
    format: 'excel',
    scope: 'teacher',
    filters: {},
    options: {
      includeConflicts: true,
      includeEmptySlots: true,
      colorCoded: true,
    },
  },
  {
    id: 'preset-3',
    name: 'KS3 Classes Only',
    description: 'Years 7-9 class timetables',
    format: 'pdf',
    scope: 'year-group',
    filters: {
      yearGroups: ['year-7', 'year-8', 'year-9'],
    },
    options: {
      includeConflicts: false,
      includeEmptySlots: true,
      colorCoded: true,
      pageOrientation: 'portrait',
    },
  },
  {
    id: 'preset-4',
    name: 'Room Schedule CSV',
    description: 'Room allocations for data analysis',
    format: 'csv',
    scope: 'room',
    filters: {},
    options: {
      includeConflicts: false,
      includeEmptySlots: false,
    },
  },
];

export const mockExportHistory: ExportHistory[] = [
  {
    id: 'export-1',
    timestamp: '2025-01-16T14:30:00Z',
    format: 'PDF',
    scope: 'Full Timetable',
    fileName: 'Timetable_Autumn_2025_Full.pdf',
    fileSize: '2.4 MB',
    downloadedBy: 'admin-1',
    downloadedByName: 'John Smith',
  },
  {
    id: 'export-2',
    timestamp: '2025-01-16T11:15:00Z',
    format: 'Excel',
    scope: 'Teacher Timetables',
    fileName: 'Teacher_Timetables_2025.xlsx',
    fileSize: '1.8 MB',
    downloadedBy: 'admin-1',
    downloadedByName: 'John Smith',
  },
  {
    id: 'export-3',
    timestamp: '2025-01-15T16:45:00Z',
    format: 'PDF',
    scope: 'Year 7',
    fileName: 'Year7_Timetable.pdf',
    fileSize: '456 KB',
    downloadedBy: 'admin-2',
    downloadedByName: 'Sarah Wilson',
  },
  {
    id: 'export-4',
    timestamp: '2025-01-15T09:00:00Z',
    format: 'CSV',
    scope: 'Room Schedule',
    fileName: 'Room_Schedule_Export.csv',
    fileSize: '128 KB',
    downloadedBy: 'admin-1',
    downloadedByName: 'John Smith',
  },
];

export const yearGroupOptions = [
  { id: 'year-7', label: 'Year 7' },
  { id: 'year-8', label: 'Year 8' },
  { id: 'year-9', label: 'Year 9' },
  { id: 'year-10', label: 'Year 10' },
  { id: 'year-11', label: 'Year 11' },
  { id: 'year-12', label: 'Year 12' },
  { id: 'year-13', label: 'Year 13' },
];

export const getExportStats = () => ({
  totalExports: mockExportHistory.length,
  recentExports: mockExportHistory.filter(
    (e) => new Date(e.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  ).length,
  savedPresets: mockExportPresets.length,
  formatsAvailable: exportFormats.length,
});

